import React, {useState, useEffect} from "react";
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {useSearchParams} from "react-router-dom";
import {toast} from "react-toastify";
import {Switch} from '@headlessui/react';
import {
    ArrowPathIcon,
    HashtagIcon,
    LockClosedIcon,
    BuildingOfficeIcon,
    ArrowTopRightOnSquareIcon,
    ExclamationCircleIcon
} from "@heroicons/react/20/solid";
import {
    startSlackOAuth,
    getSlackWorkspaces,
    deleteSlackWorkspace,
    refreshSlackChannels,
    getSlackChannels,
    patchSlackChannel,
    triggerSlackChannelSync
} from "../../../api/slack";
import {getClients} from "../../../api/clients";
import {useAuth} from "../../../contexts/AuthContext";
import {PrimaryButton, SecondaryButton} from "../../elements/Buttons";
import {DeleteButton} from "../../elements/DeleteButton";
import {Select} from "../../elements/forms/Inputs";
import BasicModal from "../../elements/modal";

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

export default function SlackIntegration() {
    const {currentUser} = useAuth();
    const queryClient = useQueryClient();
    const [searchParams] = useSearchParams();
    const [selectedWorkspace, setSelectedWorkspace] = useState(null);
    const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
    const [workspaceToDisconnect, setWorkspaceToDisconnect] = useState(null);
    const [refreshingWorkspaces, setRefreshingWorkspaces] = useState({});
    const [syncingChannels, setSyncingChannels] = useState({});

    // Check for OAuth callback
    useEffect(() => {
        const success = searchParams.get('success');
        const error = searchParams.get('error');
        const workspaceId = searchParams.get('workspace_id');

        if (success === 'true' && workspaceId) {
            toast.success('Slack workspace connected successfully!');
            queryClient.invalidateQueries(['slack_workspaces']);
            queryClient.invalidateQueries(['slack_channels']);
        } else if (error) {
            toast.error(`Failed to connect Slack: ${error}`);
        }
    }, [searchParams, queryClient]);

    // Fetch workspaces
    const {data: workspaces, isLoading: workspacesLoading} = useQuery(
        ['slack_workspaces', currentUser.id],
        getSlackWorkspaces,
        {refetchInterval: 1000 * 60 * 2}
    );

    // Fetch channels
    const channelParams = selectedWorkspace ? {workspace: selectedWorkspace} : {};
    const {data: channels, isLoading: channelsLoading} = useQuery(
        ['slack_channels', currentUser.id, selectedWorkspace],
        () => getSlackChannels(channelParams),
        {refetchInterval: 1000 * 60 * 2}
    );

    // Fetch clients for mapping
    const {data: clients} = useQuery(
        ['clients', currentUser.id],
        getClients,
        {refetchInterval: 1000 * 60 * 60}
    );

    // Connect workspace via OAuth
    async function handleConnectWorkspace() {
        try {
            const response = await startSlackOAuth();
            if (response.oauth_url) {
                window.location.href = response.oauth_url;
            }
        } catch (error) {
            toast.error('Failed to start OAuth flow');
        }
    }

    // Reconnect to update permissions (same OAuth flow - will update existing workspace)
    async function handleReconnect() {
        try {
            const response = await startSlackOAuth();
            if (response.oauth_url) {
                window.location.href = response.oauth_url;
            }
        } catch (error) {
            toast.error('Failed to start OAuth flow');
        }
    }

    // Disconnect workspace
    async function handleDisconnectWorkspace() {
        if (!workspaceToDisconnect) return;
        try {
            await deleteSlackWorkspace(workspaceToDisconnect.id);
            toast.success(`Disconnected ${workspaceToDisconnect.team_name}`);
            queryClient.invalidateQueries(['slack_workspaces']);
            queryClient.invalidateQueries(['slack_channels']);
            setDisconnectModalOpen(false);
            setWorkspaceToDisconnect(null);
        } catch (error) {
            toast.error('Failed to disconnect workspace');
        }
    }

    // Refresh channels for a workspace
    async function handleRefreshChannels(workspace) {
        setRefreshingWorkspaces(prev => ({...prev, [workspace.id]: true}));
        try {
            await refreshSlackChannels(workspace.id);
            toast.success('Channel refresh started. Please wait a few seconds.');
            setTimeout(() => {
                queryClient.invalidateQueries(['slack_channels']);
            }, 3000);
        } catch (error) {
            toast.error('Failed to refresh channels');
        } finally {
            setRefreshingWorkspaces(prev => ({...prev, [workspace.id]: false}));
        }
    }

    // Toggle sync for a channel
    async function handleToggleSync(channel) {
        try {
            await patchSlackChannel(channel.id, {is_sync_enabled: !channel.is_sync_enabled});
            queryClient.invalidateQueries(['slack_channels']);
            toast.success(`Sync ${!channel.is_sync_enabled ? 'enabled' : 'disabled'} for #${channel.channel_name}`);
        } catch (error) {
            toast.error('Failed to update channel');
        }
    }

    // Update client mapping for a channel
    async function handleClientChange(channel, clientId) {
        try {
            await patchSlackChannel(channel.id, {client: clientId});
            queryClient.invalidateQueries(['slack_channels']);
            toast.success(`Updated client mapping for #${channel.channel_name}`);
        } catch (error) {
            toast.error('Failed to update client mapping');
        }
    }

    // Trigger sync for a channel
    async function handleTriggerSync(channel) {
        setSyncingChannels(prev => ({...prev, [channel.id]: true}));
        try {
            await triggerSlackChannelSync(channel.id);
            toast.success(`Sync started for #${channel.channel_name}`);
        } catch (error) {
            if (error.response?.data?.error) {
                toast.error(error.response.data.error);
            } else {
                toast.error('Failed to trigger sync');
            }
        } finally {
            setSyncingChannels(prev => ({...prev, [channel.id]: false}));
        }
    }

    // Client options for dropdown (with "General" option)
    const clientOptions = [
        {id: null, name: 'General (No Client)'},
        ...(clients || [])
    ];

    return (
        <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
                <div className="sm:flex sm:items-center sm:justify-between">
                    <div>
                        <h3 className="text-lg font-medium leading-6 text-gray-900">
                            Slack Integration
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                            Connect your Slack workspace to sync messages and map channels to clients.
                        </p>
                    </div>
                    <div className="mt-4 sm:mt-0">
                        <PrimaryButton
                            label="Connect Workspace"
                            onClick={handleConnectWorkspace}
                        />
                    </div>
                </div>

                {/* Workspaces Section */}
                {workspacesLoading ? (
                    <div className="mt-6 text-center text-gray-500">Loading workspaces...</div>
                ) : workspaces && workspaces.length > 0 ? (
                    <div className="mt-6">
                        <h4 className="text-sm font-medium text-gray-900 mb-4">Connected Workspaces</h4>
                        <div className="space-y-3">
                            {workspaces.map(workspace => (
                                <div
                                    key={workspace.id}
                                    className={classNames(
                                        "border rounded-lg p-4 flex items-center justify-between",
                                        selectedWorkspace === workspace.id ? "border-indigo-500 bg-indigo-50" : "border-gray-200"
                                    )}
                                >
                                    <div
                                        className="flex-1 cursor-pointer"
                                        onClick={() => setSelectedWorkspace(
                                            selectedWorkspace === workspace.id ? null : workspace.id
                                        )}
                                    >
                                        <div className="flex items-center">
                                            <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-2"/>
                                            <span className="font-medium text-gray-900">
                                                {workspace.team_name}
                                            </span>
                                            {!workspace.is_active && (
                                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                                    Inactive
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-1 text-sm text-gray-500">
                                            {workspace.enabled_channel_count} of {workspace.channel_count} channels enabled
                                            {workspace.last_polled && (
                                                <span className="ml-2">
                                                    | Last synced: {new Date(workspace.last_polled).toLocaleString()}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <SecondaryButton
                                            label="Refresh Channels"
                                            onClick={() => handleRefreshChannels(workspace)}
                                            disabled={refreshingWorkspaces[workspace.id]}
                                            icon={
                                                <ArrowPathIcon
                                                    className={classNames(
                                                        "h-4 w-4 mr-1",
                                                        refreshingWorkspaces[workspace.id] ? "animate-spin" : ""
                                                    )}
                                                />
                                            }
                                        />
                                        <SecondaryButton
                                            label="Update Permissions"
                                            onClick={() => handleReconnect()}
                                            title="Re-authorize to grant new permissions (e.g., for canvas sync)"
                                        />
                                        <DeleteButton
                                            onDelete={() => {
                                                setWorkspaceToDisconnect(workspace);
                                                setDisconnectModalOpen(true);
                                            }}
                                            label="Disconnect"
                                            confirmPrompt={`Are you sure you want to disconnect ${workspace.team_name}?`}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="mt-6 text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                        <HashtagIcon className="mx-auto h-12 w-12 text-gray-400"/>
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No workspaces connected</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            Get started by connecting your Slack workspace.
                        </p>
                    </div>
                )}

                {/* Channels Section */}
                {workspaces && workspaces.length > 0 && (
                    <div className="mt-8">
                        <div className="sm:flex sm:items-center sm:justify-between mb-4">
                            <h4 className="text-sm font-medium text-gray-900">Channels</h4>
                            {workspaces.length > 1 && (
                                <div className="mt-2 sm:mt-0">
                                    <select
                                        className="block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                                        value={selectedWorkspace || ''}
                                        onChange={(e) => setSelectedWorkspace(e.target.value ? parseInt(e.target.value) : null)}
                                    >
                                        <option value="">All Workspaces</option>
                                        {workspaces.map(ws => (
                                            <option key={ws.id} value={ws.id}>{ws.team_name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        {channelsLoading ? (
                            <div className="text-center text-gray-500 py-4">Loading channels...</div>
                        ) : channels && channels.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-300">
                                    <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">
                                            Channel
                                        </th>
                                        {workspaces.length > 1 && !selectedWorkspace && (
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Workspace
                                            </th>
                                        )}
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            Client
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            Sync Enabled
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            Last Synced
                                        </th>
                                        <th scope="col" className="relative py-3.5 pl-3 pr-4">
                                            <span className="sr-only">Actions</span>
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 bg-white">
                                    {channels.map((channel, index) => (
                                        <tr key={channel.id} className={index % 2 === 0 ? undefined : 'bg-gray-50'}>
                                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm">
                                                <div className="flex items-center">
                                                    {channel.is_private ? (
                                                        <LockClosedIcon className="h-4 w-4 text-gray-400 mr-1"/>
                                                    ) : (
                                                        <HashtagIcon className="h-4 w-4 text-gray-400 mr-1"/>
                                                    )}
                                                    <span className="font-medium text-gray-900">
                                                        {channel.channel_name}
                                                    </span>
                                                </div>
                                            </td>
                                            {workspaces.length > 1 && !selectedWorkspace && (
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {channel.workspace_name}
                                                </td>
                                            )}
                                            <td className="whitespace-nowrap px-3 py-4 text-sm">
                                                <Select
                                                    path="client"
                                                    options={clientOptions}
                                                    valueAttr="id"
                                                    labelAttr="name"
                                                    data={{client: channel.client}}
                                                    errors={{}}
                                                    onChange={(evt) => handleClientChange(channel, evt.target.value)}
                                                    valueOnly={true}
                                                    isClearable={false}
                                                    className="w-48"
                                                />
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm">
                                                <Switch
                                                    checked={channel.is_sync_enabled}
                                                    onChange={() => handleToggleSync(channel)}
                                                    className={classNames(
                                                        channel.is_sync_enabled ? 'bg-indigo-600' : 'bg-gray-200',
                                                        'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
                                                    )}
                                                >
                                                    <span
                                                        aria-hidden="true"
                                                        className={classNames(
                                                            channel.is_sync_enabled ? 'translate-x-5' : 'translate-x-0',
                                                            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                                                        )}
                                                    />
                                                </Switch>
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                <div className="flex items-center">
                                                    {channel.last_polled
                                                        ? new Date(channel.last_polled).toLocaleString()
                                                        : 'Never'}
                                                    {channel.last_sync_error && (
                                                        <span
                                                            className="ml-2 text-red-500 cursor-help"
                                                            title={channel.last_sync_error}
                                                        >
                                                            <ExclamationCircleIcon className="h-4 w-4" />
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm">
                                                {channel.is_sync_enabled && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleTriggerSync(channel)}
                                                        disabled={syncingChannels[channel.id]}
                                                        className="text-indigo-600 hover:text-indigo-900 inline-flex items-center"
                                                    >
                                                        <ArrowPathIcon
                                                            className={classNames(
                                                                "h-4 w-4 mr-1",
                                                                syncingChannels[channel.id] ? "animate-spin" : ""
                                                            )}
                                                        />
                                                        Sync Now
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-8 border border-gray-200 rounded-lg">
                                <p className="text-sm text-gray-500">
                                    No channels found. Click "Refresh Channels" on a workspace to fetch channels from Slack.
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Disconnect Confirmation Modal */}
            <BasicModal open={disconnectModalOpen} setOpen={setDisconnectModalOpen}>
                <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left">
                        <h3 className="text-lg font-medium leading-6 text-gray-900">
                            Disconnect Workspace
                        </h3>
                        <div className="mt-2">
                            <p className="text-sm text-gray-500">
                                Are you sure you want to disconnect <strong>{workspaceToDisconnect?.team_name}</strong>?
                                This will remove all channel mappings and stop syncing messages from this workspace.
                            </p>
                        </div>
                    </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                        type="button"
                        className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                        onClick={handleDisconnectWorkspace}
                    >
                        Disconnect
                    </button>
                    <button
                        type="button"
                        className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                        onClick={() => setDisconnectModalOpen(false)}
                    >
                        Cancel
                    </button>
                </div>
            </BasicModal>
        </div>
    );
}
